import { driver } from 'driver.js';
import { useEffect, useRef } from 'react';
import i18n from '../../../../i18n';
import { TourKey } from '../../../common/v2/tours/tours';
import { useSeenTour } from '../../../common/v2/tours/useSeenTour';
import { PBS_DRILLDOWN_CELL_CLASS } from './BalanceSheetTable';

export function usePbsDrillDownTour(
  tableRendered: boolean,
  drillDownModalOpen: boolean
) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const { seen, markSeen } = useSeenTour(TourKey.PbsDrillDownCell);

  useEffect(() => {
    if (drillDownModalOpen && driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, [drillDownModalOpen]);

  useEffect(() => {
    if (!tableRendered || drillDownModalOpen) return;
    if (seen) return;

    const timeout = setTimeout(() => {
      const firstCell = document.querySelector(`.${PBS_DRILLDOWN_CELL_CLASS}`);
      if (!firstCell) return;

      markSeen();

      const driverObj = driver({
        showProgress: false,
        animate: true,
        allowClose: true,
        overlayColor: 'rgba(0, 0, 0, 0.55)',
        stagePadding: 6,
        stageRadius: 4,
        popoverClass: 'pbs-tour-popover',
        steps: [
          {
            element: `.${PBS_DRILLDOWN_CELL_CLASS}`,
            popover: {
              title: i18n.t(
                'pages:dashboards.peopleBalanceSheet.tour.cellClickTitle',
                { defaultValue: 'Click any value cell' }
              ),
              description: i18n.t(
                'pages:dashboards.peopleBalanceSheet.tour.cellClickDescription',
                {
                  defaultValue:
                    'Click on any number in the table to see the list of underlying employees for that metric, period, and segment.',
                }
              ),
              side: 'bottom',
              align: 'center',
            },
          },
        ],
      });

      driverRef.current = driverObj;
      driverObj.drive();
    }, 800);

    return () => clearTimeout(timeout);
  }, [tableRendered, drillDownModalOpen, seen, markSeen]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, []);
}
